import React, { useContext, useState } from 'react';
import './EledoInputFields.scss';
import {useDispatch, useSelector} from "react-redux";
import {useShowMenu} from "hooks";
import { PlatformContext } from 'PlatformContext';
import { RootState, useAppDispatch } from '_store';
import utils from 'utils';
import { DataModel, DataModelClass, DataModelField } from 'graphql/DataModelQueries';
import { EledoModal } from 'components/library';
import EledoConnectModal, { EledoConnectForm } from './EledoConnectModal';
import { connectTemplate } from '_reducers/templateReducer';

const EledoInputFields = ({dataModel}: {dataModel: DataModel}) => {
  const platform = useContext(PlatformContext);
  const isEmbedded = platform.isEmbedded;
  const [ templateId, connectionId, structureId, schemaModel ] = useSelector<RootState, any>(state => [
    state.templates.selectedTemplate?.id,
    state.templates.selectedTemplate?.connectionId,
    state.templates.selectedTemplate?.structureId,
    state.templates.selectedTemplate?.derived?.schemaModel ]);
  const [connectModalDisplayed, setConnectModalDisplayed] = useState(false)
  const dispatch = useAppDispatch();

  const dataModelImported = dataModel?.classId !== null

  const connectTemplateAction = ({connectionId, structureId}: EledoConnectForm) => {
    if(connectionId && structureId){
      dispatch((connectTemplate as (
        args: { templateId: string; platform: string; connectionId: string; structureId: string }
      ) => any)({templateId, platform: platform.platform, connectionId, structureId}));
    }

    setConnectModalDisplayed(false)
  }

  const getFieldsDiff = (dataModel: DataModel, schemaModel: DataModel): TemplateField[] => {
    if(dataModel === null){
      return []
    }

    const result = [] as TemplateField[]
    try {
      const dataStack = [], schemaStack = [], parentStack = [] as string[]
  
      let dataClass = dataModel.classes?.find(c => c.classId === dataModel.classId)
      let schemaClass: DataModelClass | null | undefined = schemaModel.classes?.find(c => c.classId === schemaModel.classId)
      
      dataStack.push(dataClass)
      schemaStack.push(schemaClass)
  
      
      do {
        dataClass = dataStack.pop()
        schemaClass = schemaStack.pop()
  
        while(schemaClass === null){
          parentStack.pop()
          schemaClass = schemaStack.pop()
        }
  
        if(schemaClass && schemaClass.id !== null){
          schemaStack.push(null)
          parentStack.push(schemaClass.id)
        }
  
        schemaClass?.fields.forEach(field => {
          const dataMatch = dataClass?.fields?.find(a => a.id === field.id)
          if(dataMatch === undefined){
            if(schemaClass)
              addSubtree(result, schemaModel, schemaClass, field, parentStack.join('.'))
          } else {
            if(field.relClass !== undefined && field.relClass !== null){
              dataStack.push(dataModel.classes?.find(c => c.classId === dataMatch.relClass))
              schemaStack.push(schemaModel.classes.find(c => c.classId === field.relClass))
              
            }
          }
        })
  
      } while (schemaStack.length > 0)

      } catch(e){
      }
      
      return result;
  }
  
  const addSubtree = (result: TemplateField[], schemaModel: DataModel, schemaClass: DataModelClass, f: DataModelField, prefix: string) => {

    if(f.relClass !== undefined && f.relClass !== null){
      let c = schemaModel.classes.find(c => c.classId === f.relClass)
      if(c !== undefined){
        if(f.type === 'Array') {
          //f = c.fields[0]
         // c = schemaModel.classes.find(c => c.id === f.relClass)
        }
        c?.fields.forEach(field => {
          if(c !== undefined)
            addSubtree(result, schemaModel, c, field, (prefix ? prefix + "." : "") + f.id)
        })
      }
    } else {
      const field = {
        name: (prefix ? prefix + "." : "") + f.id,
        label: f.name,
        type: f.type,
        extType: f.extType
      } as TemplateField

      result.push(field)
    }
  }

  const fieldsNotMatchingDataModel = getFieldsDiff(dataModel, schemaModel)

  useShowMenu()

  return (
    <div className="EledoInputFields" data-testid="EledoInputFields">
      { !isEmbedded && 
        <div className="section section-small">
          <h3>Import Data Structure</h3>
          <p>
            Retrieve data structure from your connected data source to make template building easier.
          </p>
          <button className="button w-button" onClick={() => setConnectModalDisplayed(true)}>Open Connections</button>
          <EledoConnectModal connectionId={connectionId} structureId={structureId} modalDisplayed={connectModalDisplayed} onSuccess={(data) => connectTemplateAction(data)} onDismiss={() => setConnectModalDisplayed(false)}/>
        </div>
      }

      { dataModel !== undefined && dataModel !== null &&
        <div className="section section-small">
          <h3>{ dataModelImported &&  "Imported" } Data Model</h3>
          <Tree dataModel={dataModel} />
        </div>
      }

      { fieldsNotMatchingDataModel && fieldsNotMatchingDataModel.length > 0 &&
        <div className="section section-small">
          <h3>Fields not matching imported data model</h3>
          { renderFieldTable(fieldsNotMatchingDataModel) }
        </div>
      }
    </div>
  );
}

// const filterSearch = (children: any, needle: string) => {
//   return children.name?.indexOf(needle) >= 0 || children.title?.toLowerCase().indexOf(needle?.toLowerCase()) >= 0
// }

type TreeLayerProps = {
  dataModel: DataModel
  fields: [DataModelField] | undefined
  context: string
  depth: number
}

const Tree = ({dataModel}: {dataModel: DataModel}) => {
  const fields = dataModel.classes?.find(c => c.classId === dataModel.classId)?.fields

  return (
    <TreeLayer dataModel={dataModel} fields={fields} context={""} depth={0} />
  )
}

const TreeLayer = ({dataModel, fields, context, depth}: TreeLayerProps) => {
  const [expanded, setExpanded] = useState<{[key: string]: boolean}>({})

  const toggleExpanded = (name: string) => {
    let newCollapsed = utils.deepCopy(expanded)
    newCollapsed[name] = newCollapsed[name] ? !newCollapsed[name] : true
    setExpanded(newCollapsed)
  }

  const isExpanded = (name: string): boolean => {
    return expanded[name] || false;
  }

  return (
    <div key={'ul_' + context} className={'tree-layer'}>
      { fields && fields.map(children => (
        <div key={'li_' + depth + '.' + children.id + '.' + children.type}>
          <div className={'item'} onClick={() => toggleExpanded(children.id)}>
            { depth > 0 && [...Array(depth)].map((e, i) =>
              <div key={'lig_' + depth + '.' + children.id + '.' + children.type + i} style={{width: '24px', textAlign: 'center'}}><div style={{borderLeft: '1px solid black', height:'100%', width: 0, margin: 'auto'}}/></div>
              )
            }
            { (children.type === 'Array' || children.type === 'Object') ?
              <div style={{width: '24px', textAlign: 'center'}}>+</div> :
              <div style={{width: '24px', textAlign: 'center'}}><div style={{borderLeft: '1px solid black', height:'100%', width: 0, margin: 'auto'}}/></div>
            }
            <div className='title'>
              <span>{context + (context ? '.' : '') + children.id}</span>&nbsp;<span style={{color: 'lightgray'}}>({children.type})</span>
            </div>
            <div className='ext'>
              <span style={{textAlign: 'right'}}>{children.name}</span>
            </div>
          </div>
          <div>
            { isExpanded(children.id) && (children.type === 'Array' || children.type === 'Object') &&
              <>
                <TreeLayer dataModel={dataModel} fields={dataModel.classes?.find(c => c.classId === children.relClass)?.fields} context={context + (context ? '.' : '') + children.name} depth={depth + 1}/>
              </>
            }
          </div>
        </div>
      ))}
    </div>
  )
}

type TemplateField = {
  name: string
  type: string
  extType: string
  label: string
}

const renderFieldTable = (fields: TemplateField[]) => (
  fields && fields.length > 0 ?
    <div>
      <div className="w-layout-grid header-grid no-top-margin">
        <div className="_wf-table-cell">Field</div>
        <div className="_wf-table-cell">Type</div>
        <div className="_wf-table-cell">External Type</div>
        <div className="_wf-table-cell">Label</div>
      </div>
      { fields.map(f => (
        <div className="w-layout-grid data-grid" key={f.name}>
          <div className="_wf-table-cell">
            <div className="tile-title">Field</div>
            <div>{f.name}</div>
          </div>
          <div className="_wf-table-cell">
            <div className="tile-title">Type</div>
            <div>{f.type}</div>
          </div>
          <div className="_wf-table-cell">
            <div className="tile-title">External Type</div>
            <div>{f.extType}</div>
          </div>
          <div className="_wf-table-cell">
            <div className="tile-title">Label</div>
            <div>{f.label}</div>
          </div>
        </div>
      )) }
    </div>
    : <p className="no-fields">No fields defined yet.</p>
);

export default EledoInputFields;
